// src/Views/FAQView.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { StoreContext } from '../contexts/StoreContext';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_MICRO, SPACING_TIGHT, SPACING_COMPACT, SPACING_COMPONENT, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_SECTION_LARGE, SPACING_HERO, SPACING_PAGE, SPACING_PAGE_LARGE } from '../constants/layout';
import {
  FONT_TITLE,
  FONT_TITLE_MOBILE,
  FONT_TITLE2,
  FONT_TITLE3,
  FONT_HEADLINE,
  FONT_BODY,
  FONT_FOOTNOTE,
} from '../constants/typography';

const CACHE_KEY = 'faqData';

function loadCache() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch {}
  return null;
}

function saveCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {}
}

function getQuestionKey(categoryId, questionId) {
  return `${categoryId}:${questionId}`;
}

export default function FAQView() {
  const { store } = useContext(StoreContext);
  const { showContact } = useDisplayPreferences();
  const [categories, setCategories] = useState(() => loadCache() || []);
  const [openIds, setOpenIds] = useState(new Set());
  const phoneNumber = store?.storeContact?.phoneNumber?.trim() || '';
  const phoneHref = phoneNumber ? `tel:${phoneNumber.replace(/[^+\d]/g, '')}` : '';
  const emailAddress = store?.storeContact?.email?.trim() || '';

  const totalQuestions = useMemo(
    () => categories.reduce((sum, category) => sum + (category.questions?.length || 0), 0),
    [categories]
  );

  useEffect(() => {
    document.title = 'FAQ';
  }, []);

  useEffect(() => {
    let isCancelled = false;

    async function fetchFAQ() {
      try {
        const categoriesQuery = query(
          collection(db, 'faq_categories'),
          orderBy('order', 'asc')
        );
        const categoriesSnapshot = await getDocs(categoriesQuery);
        const cats = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          questions: [],
        }));

        const withQuestions = await Promise.all(
          cats.map(async (cat) => {
            const questionsQuery = query(
              collection(db, 'faq_categories', cat.id, 'questions'),
              orderBy('order', 'asc')
            );
            const questionsSnapshot = await getDocs(questionsQuery);
            const questions = questionsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            return { ...cat, questions };
          })
        );

        if (isCancelled) return;

        setCategories(withQuestions);
        saveCache(withQuestions);
      } catch (error) {
        if (!isCancelled) {
          console.error('Error fetching FAQ:', error);
        }
      }
    }

    fetchFAQ();

    return () => {
      isCancelled = true;
    };
  }, []);

  function toggleQuestion(id) {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

      return (
    <div className="faq-page">
      <div className="faq-shell">
        <div className="faq-content">
          <section className="faq-hero">
            <div className="faq-hero-copy">
              <p className="faq-eyebrow">Support</p>
              <h1 className="faq-title">Frequently Asked Questions</h1>
              <p className="faq-description">
                Find quick answers about ordering, care, and what to expect. Browse by
                topic below, and if you still need help, we can point you in the right
                direction.
              </p>
            </div>

            <aside className="faq-summary-card" aria-label="FAQ summary">
              <div className="faq-summary-stat">
                <span className="faq-summary-number">{categories.length}</span>
                <span className="faq-summary-label">
                  {categories.length === 1 ? 'Topic' : 'Topics'}
                </span>
              </div>
              <div className="faq-summary-divider" />
              <div className="faq-summary-stat">
                <span className="faq-summary-number">{totalQuestions}</span>
                <span className="faq-summary-label">
                  {totalQuestions === 1 ? 'Answer' : 'Answers'}
                </span>
              </div>
            </aside>
          </section>

          {categories.length > 1 && (
            <nav className="faq-jump-links" aria-label="FAQ topics">
              {categories.map(category => (
                <a
                  key={category.id}
                  href={`#faq-category-${category.id}`}
                  className="faq-jump-link"
                >
                  {category.title}
                </a>
              ))}
            </nav>
          )}

          {categories.length === 0 ? (
            <section className="faq-empty-card">
              <span className="material-icons faq-empty-icon" aria-hidden="true">
                help_outline
              </span>
              <h2 className="faq-empty-title">No FAQ entries yet</h2>
              <p className="faq-empty-description">
                This page is ready to go once your FAQ categories and questions are added in
                Firestore.
              </p>
            </section>
          ) : (
            <div className="faq-sections">
              {categories.map(category => (
                <section
                  key={category.id}
                  id={`faq-category-${category.id}`}
                  className="faq-section-card"
                >
                  <div className="faq-section-header">
                    <div>
                      <p className="faq-section-kicker">Topic</p>
                      <h2 className="faq-section-title">{category.title}</h2>
                      {category.subtitle && (
                        <p className="faq-section-subtitle">{category.subtitle}</p>
                      )}
                    </div>
                    <div className="faq-section-count">
                      {category.questions.length}{' '}
                      {category.questions.length === 1 ? 'question' : 'questions'}
                    </div>
                  </div>

                  {category.questions.length === 0 ? (
                    <p className="faq-section-empty">
                      Questions for this topic are coming soon.
                    </p>
                  ) : (
                    <div className="faq-accordion">
                      {category.questions.map((q, index) => {
                        const questionKey = getQuestionKey(category.id, q.id);
                        const isOpen = openIds.has(questionKey);
                        const answerId = `faq-answer-${category.id}-${q.id}`;

                        return (
                          <article key={q.id} className={`faq-item ${isOpen ? 'is-open' : ''}`}>
                            <button
                              type="button"
                              onClick={() => toggleQuestion(questionKey)}
                              className="faq-question"
                              aria-expanded={isOpen}
                              aria-controls={answerId}
                            >
                              <span className="faq-question-meta">
                                <span className="faq-question-number">
                                  {String(index + 1).padStart(2, '0')}
                                </span>
                                <span className="faq-question-text">{q.question}</span>
                              </span>
                              <span className="material-icons faq-question-icon" aria-hidden="true">
                                expand_more
                              </span>
                            </button>

                            <div
                              id={answerId}
                              className={`faq-answer-wrap ${isOpen ? 'is-open' : ''}`}
                            >
                              <div className="faq-answer-inner">
                                <p className="faq-answer-text">
                                  {q.answer || 'We are still adding an answer for this question.'}
                                </p>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}

          {(showContact || phoneHref || emailAddress) && (
            <section className="faq-help-card">
              <div className="faq-help-copy">
                <p className="faq-help-eyebrow">Still Need Help?</p>
                <h2 className="faq-help-title">We can help with the rest.</h2>
                <p className="faq-help-description">
                  If your question is not covered here, reach out and we will help you get an
                  answer quickly.
                </p>
              </div>

              <div className="faq-help-actions">
                {showContact && (
                  <Link to="/contact" className="faq-primary-action">
                    Contact Us
                  </Link>
                )}
                {phoneHref && (
                  <a href={phoneHref} className="faq-secondary-action">
                    {phoneNumber}
                  </a>
                )}
                {!phoneHref && emailAddress && (
                  <a href={`mailto:${emailAddress}`} className="faq-secondary-action">
                    {emailAddress}
                  </a>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      <style>{`
        .faq-page {
          background: var(--color-background);
          padding: ${SPACING_SECTION_LARGE} ${SPACING_CARD} ${SPACING_PAGE_LARGE};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          font-family: var(--font-family-base);
        }

        .faq-shell {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
        }

        .faq-content {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
        }

        .faq-hero {
          display: grid;
          grid-template-columns: minmax(0, 1.7fr) minmax(240px, 0.9fr);
          gap: ${SPACING_GROUP};
          align-items: stretch;
          margin-bottom: ${SPACING_CARD_BODY};
        }

        .faq-hero-copy,
        .faq-summary-card,
        .faq-section-card,
        .faq-empty-card,
        .faq-help-card {
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          background: var(--color-surface);
        }

        .faq-hero-copy {
          padding: ${SPACING_GROUP};
          background:
            linear-gradient(
              135deg,
              var(--color-surface) 0%,
              var(--color-surface-muted, var(--color-surface)) 100%
            );
        }

        .faq-eyebrow,
        .faq-section-kicker,
        .faq-help-eyebrow {
          margin: 0 0 ${SPACING_COMPONENT};
          font-size: ${FONT_FOOTNOTE};
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--color-accent);
        }

        .faq-title {
          margin: 0;
          font-size: ${FONT_TITLE};
          font-family: 'Koral Krush', Helvetica, Arial, sans-serif;
          font-weight: 800;
          line-height: 1.1;
          color: var(--color-text-primary);
        }

        .faq-description {
          margin: ${SPACING_CARD} 0 0;
          max-width: 46rem;
          font-size: ${FONT_BODY};
          line-height: 1.7;
          color: var(--color-text-secondary);
        }

        .faq-summary-card {
          padding: ${SPACING_GROUP};
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: ${SPACING_CARD};
        }

        .faq-summary-stat {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_TIGHT};
        }

        .faq-summary-number {
          font-size: ${FONT_TITLE2};
          font-weight: 800;
          line-height: 1;
          color: var(--color-text-primary);
        }

        .faq-summary-label {
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .faq-summary-divider {
          height: 1px;
          background: var(--color-border);
        }

        .faq-jump-links {
          display: flex;
          flex-wrap: wrap;
          gap: ${SPACING_COMPONENT};
          margin: 0 0 ${SPACING_GROUP};
        }

        .faq-jump-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: ${SPACING_COMPONENT} ${SPACING_BUTTON};
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          background: var(--color-surface);
          color: var(--color-text-primary);
          text-decoration: none;
          font-size: ${FONT_FOOTNOTE};
          font-weight: 600;
          transition: border-color 0.2s ease, color 0.2s ease;
        }

        .faq-jump-link:hover {
          border-color: var(--color-accent);
          color: var(--color-accent);
        }

        .faq-sections {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_GROUP};
        }

        .faq-section-card {
          padding: ${SPACING_GROUP};
          scroll-margin-top: 96px;
        }

        .faq-section-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: ${SPACING_CARD};
          margin-bottom: ${SPACING_CARD_BODY};
        }

        .faq-section-title {
          margin: 0;
          font-size: ${FONT_TITLE3};
          font-family: 'Koral Krush', Helvetica, Arial, sans-serif;
          font-weight: 800;
          color: var(--color-text-primary);
        }

        .faq-section-subtitle {
          margin: ${SPACING_COMPACT} 0 0;
          max-width: 42rem;
          font-size: ${FONT_BODY};
          line-height: 1.6;
          color: var(--color-text-secondary);
        }

        .faq-section-count {
          padding: ${SPACING_COMPACT} ${SPACING_BUTTON};
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          color: var(--color-text-secondary);
          font-size: ${FONT_FOOTNOTE};
          white-space: nowrap;
        }

        .faq-section-empty {
          margin: 0;
          color: var(--color-text-secondary);
          font-size: ${FONT_BODY};
        }

        .faq-accordion {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_BUTTON};
        }

        .faq-item {
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          background: var(--color-surface);
          overflow: hidden;
          transition: border-color 0.2s ease, background-color 0.2s ease;
        }

        .faq-item.is-open {
          border-color: var(--color-accent);
          background: var(--color-surface-muted, var(--color-surface));
        }

        .faq-question {
          width: 100%;
          border: none;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: ${SPACING_CARD};
          padding: ${SPACING_CARD} ${SPACING_CARD_BODY};
          text-align: left;
          cursor: pointer;
          color: inherit;
          font: inherit;
          font-family: var(--font-family-base);
        }

        .faq-question:hover .faq-question-text {
          color: var(--color-accent);
        }

        .faq-question-meta {
          display: flex;
          align-items: flex-start;
          gap: ${SPACING_BUTTON};
          min-width: 0;
        }

        .faq-question-number {
          min-width: 32px;
          padding-top: 1px;
          font-size: ${FONT_FOOTNOTE};
          font-weight: 700;
          color: var(--color-text-muted);
        }

        .faq-question-text {
          font-size: ${FONT_HEADLINE};
          font-weight: 700;
          line-height: 1.4;
          color: var(--color-text-primary);
          transition: color 0.2s ease;
        }

        .faq-question-icon {
          font-size: 22px;
          color: var(--color-text-secondary);
          flex-shrink: 0;
          transition: transform 0.2s ease;
          transform: rotate(0deg);
        }

        .faq-item.is-open .faq-question-icon {
          transform: rotate(180deg);
        }

        .faq-answer-wrap {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.24s ease;
        }

        .faq-answer-wrap.is-open {
          grid-template-rows: 1fr;
        }

        .faq-answer-inner {
          overflow: hidden;
        }

        .faq-answer-text {
          margin: 0;
          padding: 0 ${SPACING_CARD_BODY} ${SPACING_CARD_BODY} 66px;
          border-top: 1px solid var(--color-border);
          padding-top: ${SPACING_CARD};
          font-size: ${FONT_BODY};
          line-height: 1.7;
          color: var(--color-text-secondary);
          white-space: pre-wrap;
        }

        .faq-empty-card {
          margin: ${SPACING_COMPACT} 0 0;
          padding: ${SPACING_HERO} ${SPACING_GROUP};
          text-align: center;
        }

        .faq-empty-icon {
          font-size: 42px;
          color: var(--color-accent);
          margin-bottom: ${SPACING_BUTTON};
        }

        .faq-empty-title {
          margin: 0;
          font-size: ${FONT_TITLE3};
          font-family: 'Koral Krush', Helvetica, Arial, sans-serif;
          font-weight: 800;
          color: var(--color-text-primary);
        }

        .faq-empty-description {
          margin: ${SPACING_BUTTON} auto 0;
          max-width: 34rem;
          font-size: ${FONT_BODY};
          line-height: 1.6;
          color: var(--color-text-secondary);
        }

        .faq-help-card {
          margin-top: ${SPACING_GROUP};
          padding: ${SPACING_GROUP} ${SPACING_GROUP};
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: ${SPACING_GROUP};
        }

        .faq-help-copy {
          max-width: 42rem;
        }

        .faq-help-title {
          margin: 0;
          font-size: ${FONT_TITLE3};
          font-family: 'Koral Krush', Helvetica, Arial, sans-serif;
          font-weight: 800;
          color: var(--color-text-primary);
        }

        .faq-help-description {
          margin: ${SPACING_COMPONENT} 0 0;
          font-size: ${FONT_BODY};
          line-height: 1.6;
          color: var(--color-text-secondary);
        }

        .faq-help-actions {
          display: flex;
          flex-wrap: wrap;
          gap: ${SPACING_BUTTON};
          align-items: center;
          justify-content: flex-end;
        }

        .faq-primary-action,
        .faq-secondary-action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 46px;
          padding: 0 ${SPACING_CARD};
          border-radius: ${BORDER_RADIUS};
          text-decoration: none;
          font-size: ${FONT_FOOTNOTE};
          font-weight: 700;
          font-family: var(--font-family-base);
          transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
        }

        .faq-primary-action {
          border: 1px solid var(--color-accent);
          background: var(--color-accent);
          color: var(--color-accent-text);
        }

        .faq-primary-action:hover {
          background: var(--color-accent-hover);
          border-color: var(--color-accent-hover);
        }

        .faq-secondary-action {
          border: 1px solid var(--color-border);
          background: transparent;
          color: var(--color-text-primary);
        }

        .faq-secondary-action:hover {
          border-color: var(--color-accent);
          color: var(--color-accent);
        }

        @media (max-width: 900px) {
          .faq-hero,
          .faq-help-card {
            grid-template-columns: 1fr;
            display: grid;
          }

          .faq-help-actions {
            justify-content: flex-start;
          }
        }

        @media (max-width: 600px) {
          .faq-page {
            padding-top: ${SPACING_GROUP};
            padding-bottom: ${SPACING_PAGE};
          }

          .faq-title {
            font-size: ${FONT_TITLE_MOBILE};
          }

          .faq-hero-copy,
          .faq-summary-card,
          .faq-section-card,
          .faq-empty-card,
          .faq-help-card {
            padding: ${SPACING_CARD_BODY};
          }

          .faq-section-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .faq-question {
            padding: ${SPACING_CARD};
          }

          .faq-question-meta {
            gap: ${SPACING_COMPONENT};
          }

          .faq-question-number {
            min-width: 26px;
          }

          .faq-question-text {
            font-size: ${FONT_BODY};
          }

          .faq-answer-text {
            padding: ${SPACING_BUTTON} ${SPACING_CARD} ${SPACING_CARD} ${SPACING_CARD};
          }
        }
      `}</style>
    </div>
  );
}
